import React from 'react';

/**
 * Shared primitives for the Pro surfaces (directory, Pro page, claim flow).
 * docs/plans/2026-09-one-prompt-planner/design-listed-pros/HANDOFF.md
 *
 * Two identities, one difference: claimed = #FF00FF, listed = #9CA3AF. Every
 * place the magenta appears on a claimed page takes gray on a listed one.
 */
export const PRO_MAGENTA = '#FF00FF';
export const LISTED_GRAY = '#9CA3AF';
export const MUTED = '#6B7280';
// Green text on a light surface. #22C55E fails (3.02:1) and #16A34A measures 3.30:1; #15803D is 5.02:1 on white.
export const GREEN_ON_WHITE = '#15803D';

/* ── Glyphs — hand-stroked, square caps, currentColor. No icon library. ── */
export function Chevron({ size = 12, rotate = 0, className = '' }) {
  return (
    <svg width={size} height={Math.round(size * 0.67)} viewBox="0 0 12 8" fill="none" stroke="currentColor" strokeWidth="2"
      strokeLinecap="square" strokeLinejoin="miter" aria-hidden="true" className={className} style={rotate ? { transform: `rotate(${rotate}deg)` } : undefined}>
      <path d="M1 1 L6 6 L11 1" />
    </svg>
  );
}

export function XGlyph({ size = 14, className = '' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square" strokeLinejoin="miter" aria-hidden="true" className={className}>
      <path d="M2 2 L10 10 M10 2 L2 10" />
    </svg>
  );
}

export function CheckGlyph({ size = 18, className = '' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square" strokeLinejoin="miter" aria-hidden="true" className={className}>
      <path d="M2 6 L5 9 L10 3" />
    </svg>
  );
}

/* ── Pills ── */
/** `PRO` on magenta or `LISTED` on gray. 9px/700/0.1em, black text, 2px black border. */
export function IdentityPill({ listed, className = '', style }) {
  return (
    <span
      className={`inline-block flex-none border-2 border-black font-mono font-bold uppercase text-black ${className}`}
      style={{ fontSize: 9, letterSpacing: '0.1em', padding: '3px 6px', lineHeight: 1.1, background: listed ? LISTED_GRAY : PRO_MAGENTA, ...style }}
      data-testid={listed ? 'pill-listed' : 'pill-pro'}
    >
      {listed ? 'LISTED' : 'PRO'}
    </span>
  );
}

/** The listed cover: a solid black square with the first letter in white mono. */
export function LetterSquare({ name, size, fontSize, className = '', style }) {
  return (
    <div className={`flex flex-none items-center justify-center bg-black font-mono font-bold text-white ${className}`}
      style={{ width: size, height: size, fontSize, ...style }} aria-hidden="true">
      {(name || '?').trim().charAt(0).toUpperCase()}
    </div>
  );
}

const BTN = 'font-mono font-bold uppercase transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2';

/** CTA pill (Portal vocabulary). `fill` = black (hover magenta), else #F2F2F2 (hover black). */
export function ProButton({ fill = false, size = 'cta', className = '', style, children, ...rest }) {
  const sizes = {
    cta: { fontSize: 12, height: 50, padding: '0 26px' },
    md: { fontSize: 10, height: 46, padding: '0 20px' },
    sm: { fontSize: 10, height: 40, padding: '0 16px' },
    xs: { fontSize: 9, height: 34, padding: '0 12px' },
  };
  return (
    <button type="button"
      className={`${BTN} ${fill ? 'bg-black text-white hover:bg-[#FF00AA]' : 'bg-[#F2F2F2] text-black hover:bg-black hover:text-white'} ${className}`}
      style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', border: 'none', borderRadius: 999, letterSpacing: '0.14em', fontWeight: 800, whiteSpace: 'nowrap', ...sizes[size], ...style }} {...rest}>
      {children}
    </button>
  );
}

/** Modal shell (Portal vocabulary): white, radius 28, title row with a round ✕, optional footer row. */
export function ProSheet({ width = 560, title, onClose, children, footer, testId }) {
  return (
    <div className="fixed inset-0 safe-inset z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)' }} onClick={onClose} data-testid={testId}>
      <div className="w-full max-h-[90vh] overflow-y-auto bg-white font-mono" style={{ maxWidth: width, borderRadius: 28 }}
        onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label={title}>
        <div className="flex items-center justify-between" style={{ padding: '18px 22px 6px', gap: 10 }}>
          <span className="font-bold uppercase" style={{ fontSize: 12, letterSpacing: '0.14em' }}>{title}</span>
          {onClose && <button type="button" onClick={onClose} className="flex items-center justify-center hover:bg-black hover:text-white" aria-label="Close" style={{ width: 34, height: 34, border: 'none', borderRadius: '50%', background: '#F2F2F2', cursor: 'pointer', transition: 'background-color .2s, color .2s' }}><XGlyph size={12} /></button>}
        </div>
        {children}
        {footer && <div className="flex flex-wrap justify-end gap-2" style={{ padding: '6px 22px 22px' }}>{footer}</div>}
      </div>
    </div>
  );
}

/** Section header band: 11px/700/0.1em over a 2px rule. `right` is an optional muted note. */
export function SectionBand({ children, right }) {
  return (
    <div className="flex items-center justify-between gap-3 border-b-2 border-black bg-white" style={{ padding: '10px 16px' }}>
      <span className="font-mono font-bold uppercase" style={{ fontSize: 11, letterSpacing: '0.1em' }}>{children}</span>
      {right && <span className="font-mono font-bold uppercase" style={{ fontSize: 9, letterSpacing: '0.1em', color: LISTED_GRAY }}>{right}</span>}
    </div>
  );
}

/** Mask an email's local part to one character: c•••@domain. */
export const maskEmail = (email) => {
  if (!email || !email.includes('@')) return email || '';
  const [local, domain] = email.split('@');
  return `${local.charAt(0)}•••@${domain}`;
};

export const dollars = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;
